import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Autocomplete,
  Button,
  Tooltip,
} from "@mui/material";
import InsightsIcon from "@mui/icons-material/AutoAwesome";
import axios from "../../api/axiosInstance";
import { getHeader } from "../../types";
import InsightsModal from "./InsightsModal";

// Conversion columns — appended after "Rejected", in this exact order.
const CONVERSION_KEYS = [
  "offeredToHired",
  "interviewedToOffered",
  "interviewR1ToReject",
  "curatedToInterviewR1",
  "curatedToInterviewR2Plus",
  "curatedToScreenReject",
];

const CONVERSION_LABELS = {
  offeredToHired: "Offered → Hired",
  interviewedToOffered: "Interviewed → Offered",
  interviewR1ToReject: "R1 → Rejected",
  curatedToInterviewR1: "Curated → Interview R1",
  curatedToInterviewR2Plus: "Curated → R2+",
  curatedToScreenReject: "Curated → Screen Reject",
};

// Short initials shown in the header — hover shows the full label (CONVERSION_LABELS / base label).
const CONVERSION_SHORT = {
  offeredToHired: "OF → HI",
  interviewedToOffered: "IN → OF",
  interviewR1ToReject: "R1 → RJ",
  curatedToInterviewR1: "CU → R1",
  curatedToInterviewR2Plus: "CU → R2+",
  curatedToScreenReject: "CU → SR",
};

const BASE_SHORT = {
  uniqueJobs: "UJ",
  applied: "AP",
  curated: "CU",
  shortlisted: "SL",
  interviewed: "IN",
  offered: "OF",
  hired: "HI",
  rejected: "RJ",
};

function formatConversionLabel(key) {
  return CONVERSION_LABELS[key] || key;
}

// All columns including conversion metrics
const COLUMNS = [
  { key: "uniqueJobs", label: "Unique Jobs", short: BASE_SHORT.uniqueJobs },
  { key: "applied", label: "Applied", short: BASE_SHORT.applied },
  { key: "curated", label: "Curated", short: BASE_SHORT.curated },
  { key: "shortlisted", label: "Shortlisted", short: BASE_SHORT.shortlisted },
  { key: "interviewed", label: "Interviewed", short: BASE_SHORT.interviewed },
  { key: "offered", label: "Offered", short: BASE_SHORT.offered },
  { key: "hired", label: "Hired", short: BASE_SHORT.hired },
  { key: "rejected", label: "Rejected", short: BASE_SHORT.rejected },
  // Conversion columns
  ...CONVERSION_KEYS.map(key => ({
    key,
    label: formatConversionLabel(key),
    short: CONVERSION_SHORT[key] || key,
  })),
];

function isConversionKey(key) {
  return CONVERSION_KEYS.includes(key);
}

function totalOf(value) {
  if (value && typeof value === "object" && "total" in value) {
    return value.total;
  }
  return Number(value) || 0;
}

function subStages(value) {
  if (value && typeof value === "object") {
    return Object.entries(value).filter(
      ([key, val]) => key !== "total" && Number(val) > 0
    );
  }
  return [];
}

function dateRangeFor(dateType, customStart, customEnd) {
  const now = new Date();
  let start = new Date(now);
  let end = new Date(now);

  if (dateType === "Daily") {
    start = new Date(now);
  } else if (dateType === "Weekly") {
    start.setDate(now.getDate() - 6);
  } else if (dateType === "Monthly") {
    start.setDate(now.getDate() - 29);
  } else if (dateType === "Custom") {
    if (customStart) start = new Date(customStart);
    if (customEnd) end = new Date(customEnd);
  }

  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

export default function RolesDashboard() {
  const { company_id } = useSelector((state) => state.user.user) || {};
  const [dateType, setDateType] = useState("Weekly");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [tpFilter, setTpFilter] = useState("All");
  const [podFilter, setPodFilter] = useState("All");
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [tpOptions, setTpOptions] = useState([]);
  const [podOptions, setPodOptions] = useState([]);
  const [tpByEmail, setTpByEmail] = useState({});
  const [insightsRow, setInsightsRow] = useState(null);

  const { startDate, endDate } = useMemo(
    () => dateRangeFor(dateType, customStart, customEnd),
    [dateType, customStart, customEnd]
  );

  // Resolve tpFilter to email: if tpFilter is a name string (from freeSolo typing),
  // look up the corresponding email from tpByEmail.
  const resolvedTpEmail = useMemo(() => {
    if (tpFilter === "All") return undefined;
    // If it's already an email (contains @), use it directly
    if (tpFilter.includes("@")) return tpFilter;
    // Otherwise it's a name — look up the email
    const entry = Object.entries(tpByEmail).find(([email, name]) => name === tpFilter);
    return entry ? entry[0] : tpFilter;
  }, [tpFilter, tpByEmail]);

  useEffect(() => {
    if (!company_id) return;
    const headers = getHeader();

    axios
      .get(`/api/auth/allPodGroup/${company_id}`, { headers })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];
        const cleaned = data
          .map((p) => (p && typeof p === "object" ? p.podGroup || p.pod_group : p))
          .filter((p) => typeof p === "string" && p.trim() !== "");
        setPodOptions([...new Set(cleaned)]);
      })
      .catch((err) => console.error("Failed to load pods", err));

    axios
      .get(`/api/company/member/${company_id}`, { headers })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];
        const map = {};
        data.forEach((u) => {
          const name = [u.first_name, u.last_name].filter(Boolean).join(" ");
          const email = u.email || u.user_name || "";
          if (name.trim() && email) map[email] = name;
        });
        setTpByEmail(map);
        setTpOptions(
          Object.entries(map)
            .map(([email, name]) => ({ name, email }))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      })
      .catch((err) => console.error("Failed to load talent partners", err));

    const { startDate: initialStart, endDate: initialEnd } = dateRangeFor("Weekly");
    axios
      .post(
        "/api/dashboard/recruiter-dashboard",
        { startDate: initialStart, endDate: initialEnd, page: 1, limit: 100 },
        { headers }
      )
      .then((res) => {
        const data = Array.isArray(res.data?.result) ? res.data.result : [];
        // Only use dashboard data to supplement tpOptions if the member API
        // didn't load any options (no email mapping available).
        // Never override with name-as-email — that breaks the API payload.
      })
      .catch((err) => console.error("Failed to load dashboard names", err));
  }, [company_id]);

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("desc");
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    const payload = {
      startDate,
      endDate,
      email: resolvedTpEmail,
      pod_group: podFilter === "All" ? undefined : podFilter,
      page: 1,
      limit: 50,
    };
    if (sortBy && sortOrder) {
      payload.sortBy = sortBy;
      payload.sortOrder = sortOrder;
    }

    axios
      .post("/api/dashboard/recruiter-dashboard", payload, { headers: getHeader() })
      .then((res) => {
        if (cancelled) return;
        const data = Array.isArray(res.data?.result) ? res.data.result : [];
        setStats(data);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to fetch recruiter dashboard", err);
        setError("Failed to load dashboard data.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, resolvedTpEmail, podFilter, sortBy, sortOrder]);

  const pods_grouped = podOptions.sort();
  const tpNames = tpOptions;

  const tpSelected =
    tpFilter === "All"
      ? null
      : tpNames.find((o) => o.email === tpFilter) ||
      (typeof tpFilter === "string" ? tpFilter : null);

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
        Recruiter Dashboard
      </Typography>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2.5, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              fullWidth
              size="small"
              freeSolo
              options={tpNames}
              getOptionLabel={(o) => (typeof o === "string" ? o : o.name)}
              getOptionKey={(option) => option.email || ""}
              value={tpSelected}
              onChange={(_, val) =>
                setTpFilter(val ? (typeof val === "string" ? val : val.email) : "All")
              }
              renderInput={(params) => (
                <TextField {...params} label="Talent Partner" placeholder="Type to search"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#4F6C8D",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#4F6C8D",
                    },
                  }} />
              )}
              sx={{ minWidth: 180 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              fullWidth
              size="small"
              freeSolo
              options={pods_grouped}
              value={podFilter === "All" ? null : podFilter}
              onChange={(_, val) => setPodFilter(val || "All")}
              renderInput={(params) => (
                <TextField {...params} label="Pod" placeholder="Type to search"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-focused fieldset": {
                        borderColor: "#4F6C8D",
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#4F6C8D",
                    },
                  }} />
              )}
              sx={{ minWidth: 180 }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={9}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ flexWrap: "wrap" }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={dateType}
                onChange={(_, val) => val && setDateType(val)}
                sx={{ flexWrap: "wrap" }}
              >
                <ToggleButton value="Daily">Daily</ToggleButton>
                <ToggleButton value="Weekly">Weekly</ToggleButton>
                <ToggleButton value="Monthly">Monthly</ToggleButton>
                <ToggleButton value="Custom">Custom</ToggleButton>
              </ToggleButtonGroup>

              {dateType === "Custom" && (
                <>
                  <TextField
                    size="small"
                    type="date"
                    label="From"
                    InputLabelProps={{ shrink: true }}
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    sx={{ minWidth: 160 }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="To"
                    InputLabelProps={{ shrink: true }}
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    sx={{ minWidth: 160 }}
                  />
                </>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Table with horizontal scroll */}
      <Box sx={{ overflow: "auto", maxHeight: "70vh", borderRadius: 2, border: "1px solid #E3E3E3" }}>
        <Paper variant="outlined" sx={{ borderRadius: 0, overflow: "visible", minWidth: "max-content" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Table sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: "#FAFAFA" }}>
                  {/* First column: Talent Partner (includes Insights button, so no separate column) */}
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: 12,
                      position: "sticky",
                      top: 0,
                      left: 0,
                      bgcolor: "#FAFAFA",
                      zIndex: 3,
                      width: 150,
                      minWidth: 150,
                      maxWidth: 150,
                      px: 1,
                    }}
                  >
                    Talent Partner
                  </TableCell>
                  {COLUMNS.map((c) => {
                    const sortKey = isConversionKey(c.key) ? c.key : `${c.key}.total`;
                    const active = sortBy === sortKey;
                    return (
                      <TableCell
                        key={c.key}
                        align="center"
                        onClick={() => handleSort(sortKey)}
                        sx={{
                          fontWeight: 600,
                          cursor: "pointer",
                          userSelect: "none",
                          "&:hover": { color: "#38CFAB" },
                          fontSize: 13,
                          whiteSpace: "nowrap",
                          position: "sticky",
                          top: 0,
                          bgcolor: "#FAFAFA",
                          zIndex: 2,
                        }}
                      >
                        <Tooltip title={c.label} arrow>
                          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                            <span>{c.short}</span>
                            <span>{active ? (sortOrder === "asc" ? "▲" : "▼") : "↕"}</span>
                          </Stack>
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.map((r, i) => {
                  const hasData = totalOf(r.curated) > 0;
                  return (
                    <TableRow key={r.email || i} hover>
                      {/* Talent Partner column with avatar, name, role, and Insights button below */}
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          bgcolor: "white",
                          zIndex: 1,
                          boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
                          width: 150,
                          minWidth: 150,
                          maxWidth: 150,
                          px: 1,
                        }}
                      >
                        <Stack spacing={0} sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            sx={{ fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                          >
                            {r.name}
                          </Typography>
                          {r.role && (
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary", fontSize: 10, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                            >
                              {r.role}
                            </Typography>
                          )}
                          {/* Insights button */}
                          <Button
                            size="small"
                            startIcon={<InsightsIcon sx={{ fontSize: 14 }} />}
                            onClick={() => hasData && setInsightsRow(r)}
                            disabled={!hasData}
                            sx={{
                              textTransform: "none",
                              fontSize: 11,
                              fontWeight: 600,
                              borderRadius: 2,
                              px: 1.5,
                              py: 0.4,
                              mt: 0.5,
                              bgcolor: hasData ? "#38CFAB" : "#F0F0F0",
                              color: hasData ? "#FFFFFF" : "text.secondary",
                              "&:hover": { bgcolor: hasData ? "#2fb897" : "#F0F0F0" },
                              width: "fit-content",
                            }}
                          >
                            Insights
                          </Button>
                        </Stack>
                      </TableCell>

                      {COLUMNS.map((c) => {
                        const isInterviewed = c.key === "interviewed";
                        const isRejected = c.key === "rejected";
                        const isConversion = isConversionKey(c.key);

                        if (isInterviewed || isRejected) {
                          const subs = subStages(r[c.key]);
                          return (
                            <TableCell key={c.key} align="center">
                              <Typography variant="h7" fontWeight={c.key === "rejected" ? 400 : 500}>
                                {totalOf(r[c.key])}
                              </Typography>
                              {subs.length > 0 && (
                                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                  {subs.map(([k, v]) => (
                                    <div key={k}>{k}: {v}</div>
                                  ))}
                                </Typography>
                              )}
                            </TableCell>
                          );
                        }

                        if (isConversion) {
                          const val = r.conversionFunnel?.[c.key];
                          const display = val !== undefined && val !== null ? `${Math.round(val)}%` : "—";
                          return (
                            <TableCell key={c.key} align="center">
                              <Typography variant="body2" fontWeight={500}>
                                {display}
                              </Typography>
                            </TableCell>
                          );
                        }

                        return (
                          <TableCell key={c.key} align="center">
                            <Typography variant="body2" fontWeight={500}>
                              {totalOf(r[c.key])}
                            </Typography>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}

                {!loading && error && (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length + 1} align="center" sx={{ py: 4, color: "error.main" }}>
                      {error}
                    </TableCell>
                  </TableRow>
                )}

                {!loading && !error && stats.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length + 1} align="center" sx={{ py: 4, color: "text.secondary" }}>
                      No talent partners match the current filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Box>

      <InsightsModal
        open={Boolean(insightsRow)}
        onClose={() => setInsightsRow(null)}
        row={insightsRow}
        startDate={startDate}
        endDate={endDate}
      />
    </Box>
  );
}